import React, { useState } from 'react';
import { Card } from './ui/card';
import { Eye, ZoomIn } from 'lucide-react';
import { projectGallery, galleryCategories } from '../mockData';
import ImageLightbox from './ImageLightbox';
import BeforeAfterSlider from './BeforeAfterSlider';

// Real project photos featured in the draggable before/after strip
const BEFORE_AFTER_IDS = [1, 4, 8];

const ProjectGallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredImages = activeCategory === 'all'
    ? projectGallery
    : projectGallery.filter(img => img.category.toLowerCase() === activeCategory);

  const beforeAfterProjects = projectGallery.filter(p => BEFORE_AFTER_IDS.includes(p.id));

  const handleNext = () => {
    const currentIndex = projectGallery.findIndex(img => img.id === selectedImage.id);
    if (currentIndex < projectGallery.length - 1) {
      setSelectedImage(projectGallery[currentIndex + 1]);
    }
  };

  const handlePrev = () => {
    const currentIndex = projectGallery.findIndex(img => img.id === selectedImage.id);
    if (currentIndex > 0) {
      setSelectedImage(projectGallery[currentIndex - 1]);
    }
  };

  return (
    <section id="projects" data-testid="projects-section" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Our Premium Projects
          </h2>
          <p className="text-lg text-gray-600">
            Showcasing excellence in marble and granite work across Bangalore
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {galleryCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              data-testid={`gallery-category-${category.id.replace(/\s+/g, '-')}`}
              className={`px-6 py-2.5 rounded-full font-semibold transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-emerald-600 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.label}
              <span className="ml-2 text-sm opacity-75">({category.count})</span>
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredImages.map((image, index) => (
            <Card 
              key={image.id}
              data-testid={`gallery-item-${index}`}
              className="group overflow-hidden border-2 border-gray-100 hover:border-emerald-200 transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedImage(image)}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img 
                  src={image.image}
                  alt={image.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                
                {/* Overlay on Hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 transform scale-0 group-hover:scale-100 transition-transform duration-300">
                    <ZoomIn className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-white font-semibold text-center px-4">Click to view</p>
                </div>

                {/* Category Badge */}
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 bg-emerald-600 text-white text-xs font-bold rounded-full shadow-lg">
                    {image.category}
                  </span>
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-1 line-clamp-1">
                  {image.title}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {image.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredImages.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No projects found in this category</p>
          </div>
        )}

        {/* Before / After interactive strip */}
        <div className="mt-16 md:mt-20">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Drag to See the Difference
            </h3>
            <p className="text-gray-600">
              Drag the handle across real G.S project floors — dull stone on the left, mirror polish on the right.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {beforeAfterProjects.map((project, i) => (
              <div key={project.id} data-testid={`before-after-card-${i}`}>
                <div className="relative aspect-[4/3] overflow-hidden rounded-xl border-2 border-gray-100 shadow-lg">
                  <BeforeAfterSlider after={project.image} dullBefore testId={`portfolio-slider-${i}`} />
                </div>
                <div className="pt-3 text-center">
                  <p className="font-bold text-gray-900">{project.title}</p>
                  <p className="text-sm text-gray-600">{project.category}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 p-8 bg-emerald-50 rounded-2xl border-2 border-emerald-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Want to See More of Our Work?
          </h3>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            We have completed 500+ projects across Bangalore. Contact us to view our complete portfolio and discuss your requirements.
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid="gallery-portfolio-cta"
            className="inline-flex items-center px-8 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-lg hover:shadow-xl"
          >
            <Eye className="w-5 h-5 mr-2" />
            Request Full Portfolio
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <ImageLightbox
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
          onNext={handleNext}
          onPrev={handlePrev}
          hasNext={projectGallery.findIndex(img => img.id === selectedImage.id) < projectGallery.length - 1}
          hasPrev={projectGallery.findIndex(img => img.id === selectedImage.id) > 0}
        />
      )}
    </section>
  );
};

export default ProjectGallery;
